import { useState } from "react";
import {
  useGetAddressesQuery,
  useDeleteAddressMutation,
  useSetDefaultAddressMutation,
} from "../../store";
import { UserAddress } from "../../types/user.interfaces";
import Button from "../common/Button";
import AddressModal from "../../forms/AddressFormModal";
import ConfirmModal from "../common/ConfirmModal";
import { showToast, ToastType } from "../../utils/toast.utils";

const AddressesList = () => {
  const { data: addresses, isLoading } = useGetAddressesQuery();
  const [deleteAddress, { isLoading: isDeleting }] = useDeleteAddressMutation();
  const [setDefaultAddress] = useSetDefaultAddressMutation();

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<UserAddress | null>(
    null
  );
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null);

  const handleEditAddress = (addr: UserAddress) => {
    setEditingAddress(addr);
    setIsAddressModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setAddressToDelete(id);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (addressToDelete) {
      const result = await deleteAddress(addressToDelete);
      if (!result.error) {
        showToast("Adresse supprimée", ToastType.SUCCESS);
        setIsConfirmModalOpen(false);
        setAddressToDelete(null);
      }
    }
  };

  const handleSetDefault = async (id: string) => {
    const result = await setDefaultAddress(id);
    if (!result.error) {
      showToast("Adresse par défaut mise à jour", ToastType.SUCCESS);
    }
  };

  return (
    <div className="mt-8 bg-white p-6 rounded shadow-lg border border-gray-100">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">Mes Adresses</h2>
        <Button
          onClick={() => {
            setEditingAddress(null);
            setIsAddressModalOpen(true);
          }}
          className="bg-secondary text-primary px-4 py-2 rounded font-bold text-sm"
        >
          + Ajouter une adresse
        </Button>
      </div>

      {isLoading ? (
        <div>Chargement...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses?.map((addr) => (
            <div
              key={addr._id}
              className={`border p-4 rounded relative ${
                addr.isDefault
                  ? "border-primary bg-primary/5"
                  : "border-gray-200"
              }`}
            >
              {addr.isDefault && (
                <span className="absolute top-2 right-2 bg-primary text-white text-xs px-2 py-1 rounded">
                  Défaut
                </span>
              )}
              <h3 className="font-bold">{addr.displayName}</h3>
              <p className="text-sm text-gray-600">
                {addr.street}, {addr.zipCode} {addr.city}
              </p>
              <p className="text-xs text-gray-400">{addr.country}</p>

              <div className="mt-4 flex gap-2 text-sm">
                <button
                  onClick={() => handleEditAddress(addr)}
                  className="text-blue-600 hover:underline"
                >
                  Modifier
                </button>
                <button
                  onClick={() => handleDeleteClick(addr._id!)}
                  className="text-red-600 hover:underline"
                >
                  Supprimer
                </button>
                {!addr.isDefault && (
                  <button
                    onClick={() => handleSetDefault(addr._id!)}
                    className="text-green-600 hover:underline"
                  >
                    Définir par défaut
                  </button>
                )}
              </div>
            </div>
          ))}
          {addresses?.length === 0 && (
            <div className="col-span-2 text-center text-gray-400 italic">
              Aucune adresse enregistrée.
            </div>
          )}
        </div>
      )}

      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        editingAddress={editingAddress}
        onSuccess={() => {
          setIsAddressModalOpen(false);
          setEditingAddress(null);
        }}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer l'adresse"
        message="Êtes-vous sûr de vouloir supprimer cette adresse ? Cette action est irréversible."
        confirmLabel="Supprimer"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AddressesList;
